<template>

    <ion-button @click="onClick" :size="size" :shape="shape" :fill="fill" :color="color">
        <slot></slot>
        <input
            ref="uploader"
            type="file"
            :multiple="multiple"
            :accept="accept"
            @change="onFileChanged"
        >
    </ion-button>


</template>

<script>
import { defineComponent, ref } from 'vue';
import { IonButton } from '@ionic/vue';
import openToast from "@/services/ToastService";
import { compressImage } from "@/services/ImageService";

// Limite acceptée par le back : `max:20000` côté validation Laravel, qui
// compte en kilo-octets de 1024 octets (soit 19,5 Mio, pas 20).
const MAX_FILE_SIZE = 20000 * 1024;

export default defineComponent({
    name: 'ButtonUpload',
    components: {
        IonButton
    },
    props: {
        size: String,
        shape: String,
        fill: String,
        color: String,
        multiple: Boolean,
        callback: Function,
        accept: String,
        maxSize: {
            type: Number,
            default: MAX_FILE_SIZE
        },
    },
    emits: ['click'],
    setup(props, { emit }) {

        const uploader = ref(null);

        const onClick = () => {
            emit('click');
            uploader.value.click();
        }

        const getPreview = (image) => {
            return new Promise(function (resolve) {
                let reader = new FileReader();
                reader.onload = function (e) {
                    resolve(e.target.result);
                };
                reader.readAsDataURL(image);
            });
        }

        const formatSize = (size) => {
            return (size / (1024 * 1024)).toFixed(1).replace('.', ',') + ' Mo';
        }

        const onFileChanged = async (e) => {
            const input = e.target;
            let files = input.files;
            let dataFile = [];

            // Le champ est vidé tout de suite : le callback peut démonter ce
            // composant (fermeture de la modale) avant la fin du traitement.
            const resetInput = () => { input.value = null; };

            if (!files || files.length === 0) return null;

            for (const file of files) {
                // Le poids est contrôlé après compression : c'est ce fichier-là
                // qui part vers le back, une photo brute de 30 Mo qui retombe
                // à 2 Mo n'a aucune raison d'être refusée.
                const optimized = await compressImage(file);

                if (optimized.size > props.maxSize) {
                    resetInput();
                    openToast("Le fichier « " + file.name + " » pèse " + formatSize(optimized.size)
                        + " et dépasse la limite de " + formatSize(props.maxSize) + ".");
                    return null;
                }

                let df = { file: optimized };
                df.preview = await getPreview(optimized);
                dataFile.push(df);
            }

            resetInput();

            if (props.callback) props.callback(dataFile);
        }

        return {
            uploader,
            onFileChanged,
            onClick
        }
    }
});
</script>

<style lang="scss" scoped>
    input {
        display: none;
    }
    ion-button {
        height: auto;
        --padding-top: 8px;
        --padding-bottom: 8px;
        white-space: normal;

        &.button-large {
            --padding-top: 16px;
            --padding-bottom: 16px;
        }
    }
</style>